import DeleteIcon from '@mui/icons-material/Delete'
import CopyIcon from '@mui/icons-material/FileCopy'
import { AccordionDetails, Box, Button, Grid, IconButton } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useLoggedInUser } from '../../../app/hooks/useLoggedInUser'
import {
  useGetAllLoadTemplatesQuery,
  useGetAllLoadTypesQuery,
  useGetLoadTemplateByIdQuery,
} from '../../../app/redux-fetch/apiOrders'
import { convertToLabelValue } from '../../../app/utils'
import StyledAccordion from '../../../components/Accordions/StyledAccordion'
import StyledAccordionContainer from '../../../components/Accordions/StyledAccordionContainer'
import StyledAccordionSummary from '../../../components/Accordions/StyledAccordionSummary'
import DefaultSpacingGrid from '../../../components/Containers/DefaultSpacingGrid'
import ToggleContainer from '../../../components/Containers/ToggleContainer'
import FormInput from '../../../components/Forms/FormInput'
import FormSelect from '../../../components/Forms/FormSelect'
import SharedPhysicalProperties from '../../../components/Forms/PhysicalProperties/SharedPhysicalProperties'
import { AccordionHeader } from '../../../components/Headers/styles'
import commonMessages from '../../../components/Messages/commonMessages'
import InfoDisplayItem from '../../../components/Typographies/InfoDisplayItem'
import { LabelItem, LabelWarningItem } from '../../../components/Typographies/LabelItem'
import { LoadType } from '../../../types/common'
import { ILoadTemplate } from '../../LoadTemplate/types'
import { useOrderFormContext } from '../context/OrderFormContext'
import { useOrderStatus } from '../hooks/useOrderStatus'
import ConsignmentServiceTable from './ConsignmentService/ConsignmentServiceTable'
import PackageContentForm from './PackageContentForm'
import TemperatureControlledForm from './TemperatureControlledForm'
import messages from './messages'
import { WaypointType } from './types'

export const CUSTOM_TEMPLATE_ID = -1

interface PackagingItemProps {
  consignmentIndex: number
  packagingIndex: number
  itemId: string
  remove: (index: number) => void
  copy: (index: number) => void
  disabled?: boolean
  totalPackagingItems?: number
}

const PackagingItem: React.FC<PackagingItemProps> = ({
  consignmentIndex,
  packagingIndex,
  itemId,
  remove,
  copy,
  disabled = false,
  totalPackagingItems = 1,
}) => {
  const { watch, setValue } = useFormContext()
  const { formatMessage } = useIntl()
  const { tenantId } = useLoggedInUser()
  const packagingData = watch(`bookings.${consignmentIndex}.packaging.${packagingIndex}`) ?? {}

  const { temperatureControlled, packagingTemplateId, numberOfPackages } = packagingData

  const { variant, currentPage } = useOrderFormContext()
  const { isCreated } = useOrderStatus()

  const customerId = watch('customerId')
  const netWeight = watch(
    `bookings.${consignmentIndex}.packaging.${packagingIndex}.physicalProperties.netWeight`
  )

  const { data: packagingTypes } = useGetAllLoadTypesQuery()

  const { data: packagingTemplates = [] } = useGetAllLoadTemplatesQuery(
    {
      customerId,
    },
    { skip: !customerId }
  )

  const isReadOnly = useMemo(
    () => !!packagingTemplateId && packagingTemplateId !== CUSTOM_TEMPLATE_ID,
    [packagingTemplateId]
  )

  const palletPackagingTemplatesOptions = useMemo(
    () =>
      convertToLabelValue([...packagingTemplates.filter((x: ILoadTemplate) => x.loadTypeId === 2)]),
    [packagingTemplates]
  )

  const containerTemplateOptions = useMemo(
    () =>
      convertToLabelValue([...packagingTemplates.filter((x: ILoadTemplate) => x.loadTypeId === 1)]),
    [packagingTemplates]
  )

  const loadTypeId = watch(`bookings.${consignmentIndex}.loadTypeId`)
  const loadType: string = packagingTypes?.find((x: any) => x.id === loadTypeId)?.name ?? 'Load'

  const { data: palletTemplate } = useGetLoadTemplateByIdQuery(
    {
      id: packagingTemplateId,
      tenantId,
      customerId,
    },
    {
      skip: !packagingTemplateId || packagingTemplateId === CUSTOM_TEMPLATE_ID,
    }
  )

  useEffect(() => {
    if (packagingTemplateId === undefined) {
      setValue(`bookings.${consignmentIndex}.packaging.${packagingIndex}.packagingTemplateId`, null)
      setValue(
        `bookings.${consignmentIndex}.packaging.${packagingIndex}.physicalProperties.tareWeight`,
        0
      )
    }
  }, [packagingTemplateId, consignmentIndex, packagingIndex, setValue])

  const roundToFourDecimals = (value: any): any => {
    if (typeof value === 'number' && !isNaN(value)) {
      return Math.round(value * 10000) / 10000
    }
    return value
  }

  const roundNumericProperties = (obj: any): any => {
    if (!obj || typeof obj !== 'object') return obj

    const roundedObj = { ...obj }
    const numericFields = [
      'netWeight',
      'grossWeight',
      'height',
      'length',
      'width',
      'maxLoadingHeight',
      'tareWeight',
      'diameter',
      'volume',
      'density',
      'payloadWeight',
    ]

    numericFields.forEach((field) => {
      if (field in roundedObj) {
        roundedObj[field] = roundToFourDecimals(roundedObj[field])
      }
    })

    return roundedObj
  }

  useEffect(() => {
    if (palletTemplate?.physicalProperties) {
      const templateProperties = roundNumericProperties({ ...palletTemplate.physicalProperties })

      if (palletTemplate.loadTypeId === LoadType.pallet) {
        templateProperties.height = roundToFourDecimals(templateProperties.maxLoadingHeight)
      }
      if ('netWeight' in templateProperties) {
        templateProperties.tareWeight = roundToFourDecimals(templateProperties.netWeight)
        templateProperties.netWeight = roundToFourDecimals(netWeight)
      }

      templateProperties.useDimensions = true

      if (templateProperties.useDimensions === true) {
        templateProperties.isRectangular = templateProperties.length == null
      }

      if (templateProperties.weightMeasureUnit?.id) {
        templateProperties.weightMeasureUnitId = templateProperties.weightMeasureUnit.id
      }
      if (templateProperties.lengthMeasureUnit?.id) {
        templateProperties.lengthMeasureUnitId = templateProperties.lengthMeasureUnit.id
      }
      if (templateProperties.volumeMeasureUnit?.id) {
        templateProperties.volumeMeasureUnitId = templateProperties.volumeMeasureUnit.id
      }
      if (templateProperties.densityMeasureUnit?.id) {
        templateProperties.densityMeasureUnitId = templateProperties.densityMeasureUnit.id
      }

      setValue(
        `bookings.${consignmentIndex}.packaging.${packagingIndex}.physicalProperties`,
        templateProperties,
        { shouldValidate: true, shouldDirty: false }
      )
    }
  }, [palletTemplate, consignmentIndex, packagingIndex, setValue, netWeight])

  const shipFrom = watch(`bookings.${consignmentIndex}.shipFrom`)
  const deliverTo = watch(`bookings.${consignmentIndex}.deliverTo`)
  const waypoints: any[] = watch(`bookings.${consignmentIndex}.waypoints`) || []

  const isClearingRequired =
    shipFrom?.clearingRequired === true ||
    deliverTo?.clearingRequired === true ||
    waypoints.some((wp: any) => wp?.clearingRequired === true)

  const isCrossBorder =
    shipFrom?.transitDetails?.crossBorder === true ||
    deliverTo?.transitDetails?.crossBorder === true ||
    waypoints.some((wp: any) => wp?.transitDetails?.crossBorder === true)

  useEffect(() => {
    const currentIsClearingRequired = watch(
      `bookings.${consignmentIndex}.packaging.${packagingIndex}.isClearingRequired`
    )
    const currentIsCrossBorder = watch(
      `bookings.${consignmentIndex}.packaging.${packagingIndex}.isCrossBorder`
    )

    if (currentIsClearingRequired === undefined) {
      setValue(
        `bookings.${consignmentIndex}.packaging.${packagingIndex}.isClearingRequired`,
        isClearingRequired,
        { shouldDirty: false, shouldTouch: false, shouldValidate: false }
      )
    }
    if (currentIsCrossBorder === undefined) {
      setValue(
        `bookings.${consignmentIndex}.packaging.${packagingIndex}.isCrossBorder`,
        isCrossBorder,
        { shouldDirty: false, shouldTouch: false, shouldValidate: false }
      )
    }
  }, [isClearingRequired, isCrossBorder, consignmentIndex, packagingIndex, setValue, watch])

  const [isOpenPackageContent, setIsOpenPackageContent] = React.useState(true)

  const useDeclaredValue = watch(
    `bookings.${consignmentIndex}.packaging.${packagingIndex}.useDeclaredValue`
  )
  const products = watch(`bookings.${consignmentIndex}.packaging.${packagingIndex}.products`) ?? []

  const handleTogglePackageContent = () => {
    setIsOpenPackageContent((prev) => !prev)
    if (!useDeclaredValue && products.length === 0) {
      setValue(`bookings.${consignmentIndex}.packaging.${packagingIndex}.products`, [
        {
          productDescription: '',
          quantity: 1,
          unitPrice: 0,
          totalValue: undefined,
          useProduct: false,
        },
      ])
    } else {
      if (products.length === 0) {
        setValue(`bookings.${consignmentIndex}.packaging.${packagingIndex}.useDeclaredValue`, true)
      }
    }
  }

  const [expanded, setExpanded] = useState(true)
  const [summaryExpanded, setSummaryExpanded] = useState(true)
  const [isOpenServicesSummary, setIsOpenServicesSummary] = useState(true)

  const handleToggleAccordion = useCallback(() => {
    setExpanded((prev) => !prev)
  }, [])

  const handleToggleSummaryAccordion = useCallback(() => {
    setSummaryExpanded((prev) => !prev)
  }, [])

  const packagingTitle = `${packagingTypes?.find((x: any) => x.id === loadTypeId)?.name ??
    formatMessage(messages.consignmentLoad)
    } #${packagingIndex + 1}`

  const isParcel = loadTypeId === LoadType.parcel
  const isCrate = loadTypeId === LoadType.crate

  const renderPackagingItemForm = () => {
    if (variant === 'summary') {
      const loadId = packagingData?.id
      return (
        <StyledAccordionContainer
          expanded={summaryExpanded}
          onChange={handleToggleSummaryAccordion}
          sx={{ mb: '16px' }}
        >
          <StyledAccordionSummary
            index={`packaging-summary-${packagingIndex}`}
            expanded={summaryExpanded}
          >
            <DefaultSpacingGrid container spacing={0}>
              <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'start', mb: '2px' }}>
                <Box display='flex' alignItems='center' gap={1}>
                  <LabelItem>{packagingTitle}</LabelItem>
                  {temperatureControlled && (
                    <LabelWarningItem>
                      {formatMessage(messages.temperatureControlled)}
                    </LabelWarningItem>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <InfoDisplayItem
                  label={formatMessage(messages.noOfIdenticalLoads, { loadType: loadType })}
                  value={numberOfPackages ?? formatMessage(commonMessages.na)}
                  isSmallScreen={false}
                />
              </Grid>
              {(loadTypeId === LoadType.container || loadTypeId === LoadType.pallet) && (
                <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <InfoDisplayItem
                    label={formatMessage(
                      loadTypeId === LoadType.container
                        ? messages.containerType
                        : messages.palletType
                    )}
                    value={
                      (loadTypeId === LoadType.container
                        ? containerTemplateOptions.find((opt) => opt.value === packagingTemplateId)
                          ?.label
                        : palletPackagingTemplatesOptions.find(
                          (opt) => opt.value === packagingTemplateId
                        )?.label) ?? formatMessage(commonMessages.na)
                    }
                    isSmallScreen={false}
                  />
                </Grid>
              )}
            </DefaultSpacingGrid>
          </StyledAccordionSummary>
          <AccordionDetails>
            <ToggleContainer
              title={'Load Summary'}
              isOpen={summaryExpanded}
              handleToggle={undefined}
              isGrey={true}
              sx={{ mb: '8px', pb: '8px' }}
            >
              <SharedPhysicalProperties
                prefix={`bookings.${consignmentIndex}.packaging.${packagingIndex}`}
                isDisabled={disabled || isReadOnly}
                loadTypes={packagingTypes}
                loadTypeId={loadTypeId}
                variant='shipmentSummary'
              />
            </ToggleContainer>
            <PackageContentForm
              consignmentIndex={consignmentIndex}
              packagingIndex={packagingIndex}
              isOpenPackageContent={true}
              handleTogglePackageContent={() => {
                /* no-op in summary mode */
              }}
              disabled={disabled}
            />
            <ToggleContainer
              title={'Services'}
              isOpen={isOpenServicesSummary}
              handleToggle={() => setIsOpenServicesSummary((prev) => !prev)}
              isGrey={true}
              sx={{ mb: '16px', pb: '16px' }}
            >
              <ConsignmentServiceTable
                consignmentIndex={consignmentIndex}
                waypointType={WaypointType.ShipFrom}
                waypointPrefix={'shipFrom'}
                loadId={loadId}
              />
            </ToggleContainer>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Box sx={{ mt: '-8px' }}>
                {!isOpenServicesSummary && (
                  <Button
                    onClick={() => setIsOpenServicesSummary((prev) => !prev)}
                    sx={{ textTransform: 'none' }}
                  >
                    {'Show Services'}
                  </Button>
                )}
              </Box>
            </Box>
          </AccordionDetails>
        </StyledAccordionContainer>
      )
    }
    if (variant === 'form') {
      return (
        <StyledAccordion
          key={itemId}
          expanded={expanded}
          onChange={handleToggleAccordion}
          sx={(theme) => ({ backgroundColor: theme.palette?.accordion?.parent?.bg })}
        >
          <StyledAccordionSummary
            index={`packaging-${packagingIndex + 1}`}
            expanded={expanded}
            title={packagingTitle}
          >
            <AccordionHeader sx={{ flexGrow: 1 }}>{packagingTitle}</AccordionHeader>
            {!expanded && temperatureControlled && (
              <LabelWarningItem>{formatMessage(messages.temperatureControlled)}</LabelWarningItem>
            )}
            {!disabled && currentPage === 2 && !isCreated && (
              <>
                <IconButton onClick={() => copy(packagingIndex)} color='primary'>
                  <CopyIcon />
                </IconButton>
                {totalPackagingItems > 1 && (
                  <IconButton onClick={() => remove(packagingIndex)} color='error'>
                    <DeleteIcon />
                  </IconButton>
                )}
              </>
            )}
          </StyledAccordionSummary>
          <AccordionDetails>
            {currentPage === 2 && (
              <DefaultSpacingGrid container>
                <Grid item xs={12} md={3}>
                  <FormInput
                    name={`bookings.${consignmentIndex}.packaging.${packagingIndex}.numberOfPackages`}
                    label={formatMessage(messages.noOfIdenticalLoads, { loadType: loadType })}
                    type='number'
                    numberFormatProps={{
                      allowOnlyIntegers: true,
                      minValue: 1,
                    }}
                    fullWidth
                    maxLength={6}
                    disabled={disabled}
                  />
                </Grid>
                {(loadTypeId === LoadType.container || loadTypeId === LoadType.pallet) && (
                  <Grid item xs={12} md={3}>
                    <FormSelect
                      name={`bookings.${consignmentIndex}.packaging.${packagingIndex}.packagingTemplateId`}
                      label={formatMessage(
                        loadTypeId === LoadType.container
                          ? messages.containerType
                          : messages.palletType
                      )}
                      options={
                        loadTypeId === LoadType.container
                          ? containerTemplateOptions
                          : palletPackagingTemplatesOptions
                      }
                      onChange={handleTogglePackageContent}
                      placeholder='Please select'
                      fullWidth
                      disabled={disabled}
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={12}>
                  {(packagingTemplateId > 0 || isCrate || isParcel) && (
                    <SharedPhysicalProperties
                      prefix={`bookings.${consignmentIndex}.packaging.${packagingIndex}`}
                      isDisabled={disabled || isReadOnly}
                      loadTypes={packagingTypes}
                      loadTypeId={loadTypeId}
                      loadTemplateId={packagingTemplateId}
                      variant='packaging'
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={12}>
                  <TemperatureControlledForm
                    consignmentIndex={consignmentIndex}
                    packagingIndex={packagingIndex}
                    disabled={disabled}
                  />
                </Grid>
              </DefaultSpacingGrid>
            )}
            {currentPage === 3 && (
              <PackageContentForm
                consignmentIndex={consignmentIndex}
                packagingIndex={packagingIndex}
                isOpenPackageContent={isOpenPackageContent}
                handleTogglePackageContent={handleTogglePackageContent}
                disabled={disabled}
              />
            )}
          </AccordionDetails>
        </StyledAccordion>
      )
    }
    return null
  }

  return renderPackagingItemForm()
}

export default PackagingItem
